import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Worksheet {
  title: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
}

const worksheets: Worksheet[] = [
  // Polynomials (4 worksheets)
  { title: 'Polynomial Operations', topic: 'Polynomials', difficulty: 'Easy', pages: 2 },
  { title: 'Synthetic Division', topic: 'Polynomials', difficulty: 'Medium', pages: 2 },
  { title: 'Finding Zeros of Polynomials', topic: 'Polynomials', difficulty: 'Medium', pages: 2 },
  { title: 'Factoring Higher-Degree Polynomials', topic: 'Polynomials', difficulty: 'Hard', pages: 2 },
  
  // Rational Expressions (5 worksheets)
  { title: 'Simplifying Rational Expressions', topic: 'Rational Expressions', difficulty: 'Easy', pages: 2 },
  { title: 'Multiplying & Dividing Rationals', topic: 'Rational Expressions', difficulty: 'Medium', pages: 2 },
  { title: 'Adding & Subtracting Rationals', topic: 'Rational Expressions', difficulty: 'Medium', pages: 2 },
  { title: 'Solving Rational Equations', topic: 'Rational Expressions', difficulty: 'Hard', pages: 2 },
  { title: 'Asymptotes & Graphing Rationals', topic: 'Rational Expressions', difficulty: 'Hard', pages: 2 },
  
  // Exponential & Logarithms (4 worksheets)
  { title: 'Exponential Growth & Decay', topic: 'Exponential & Logarithms', difficulty: 'Easy', pages: 2 },
  { title: 'Logarithm Properties', topic: 'Exponential & Logarithms', difficulty: 'Medium', pages: 2 },
  { title: 'Solving Exponential & Log Equations', topic: 'Exponential & Logarithms', difficulty: 'Hard', pages: 2 },
  { title: 'Graphing Exponential & Log Functions', topic: 'Exponential & Logarithms', difficulty: 'Medium', pages: 2 },
  
  // Sequences & Series (4 worksheets)
  { title: 'Arithmetic Sequences & Series', topic: 'Sequences & Series', difficulty: 'Easy', pages: 2 },
  { title: 'Geometric Sequences & Series', topic: 'Sequences & Series', difficulty: 'Medium', pages: 2 },
  { title: 'Sigma Notation', topic: 'Sequences & Series', difficulty: 'Medium', pages: 2 },
  { title: 'Binomial Theorem', topic: 'Sequences & Series', difficulty: 'Hard', pages: 2 },
  
  // Trigonometry (4 worksheets)
  { title: 'Unit Circle & Trig Values', topic: 'Trigonometry', difficulty: 'Easy', pages: 2 },
  { title: 'Trigonometric Identities', topic: 'Trigonometry', difficulty: 'Medium', pages: 2 },
  { title: 'Solving Trig Equations', topic: 'Trigonometry', difficulty: 'Hard', pages: 2 },
  { title: 'Graphing Trig Transformations', topic: 'Trigonometry', difficulty: 'Medium', pages: 2 },
  
  // Statistics & Probability (4 worksheets)
  { title: 'Normal Distribution', topic: 'Statistics & Probability', difficulty: 'Medium', pages: 2 },
  { title: 'Conditional Probability', topic: 'Statistics & Probability', difficulty: 'Hard', pages: 2 },
  { title: 'Permutations & Combinations', topic: 'Statistics & Probability', difficulty: 'Medium', pages: 2 },
  { title: 'Data Analysis & Interpretation', topic: 'Statistics & Probability', difficulty: 'Easy', pages: 2 },
  
  // Conic Sections (2 worksheets)
  { title: 'Circles & Parabolas', topic: 'Conic Sections', difficulty: 'Medium', pages: 2 },
  { title: 'Ellipses & Hyperbolas', topic: 'Conic Sections', difficulty: 'Hard', pages: 2 },
];

const scheduleOptions = [
  { weeks: 4, label: '4-Week Intensive', description: 'Fast-paced review' },
  { weeks: 6, label: '6-Week Balanced', description: 'Recommended pace' },
  { weeks: 8, label: '8-Week Thorough', description: 'Deep understanding' },
  { weeks: 12, label: '12-Week Comprehensive', description: 'Mastery-focused' },
];

const studyTips = [
  {
    icon: 'ri-book-open-line',
    title: 'Master the Fundamentals',
    description: 'Polynomials and rational expressions build on Algebra 1. Review factoring and equation-solving before diving into advanced topics.',
  },
  {
    icon: 'ri-calculator-line',
    title: 'Practice Daily',
    description: 'Aim for 30-45 minutes per day. Consistent practice with logarithms, trig identities, and sequences is key to retention.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Visualize Concepts',
    description: 'Use graphing tools for exponential functions, trig graphs, and conic sections. Visual understanding deepens comprehension.',
  },
  {
    icon: 'ri-team-line',
    title: 'Study Groups',
    description: 'Collaborate on challenging topics like binomial theorem and conditional probability. Teaching others reinforces your understanding.',
  },
];

export default function Grade11StudyPlanPage() {
  const [selectedWeeks, setSelectedWeeks] = useState(6);

  const worksheetsPerWeek = Math.ceil(worksheets.length / selectedWeeks);
  const totalPages = worksheets.reduce((sum, w) => sum + w.pages, 0);
  const estimatedHours = Math.round((totalPages * 0.5 + worksheets.length * 0.25) * 10) / 10;

  const getWeeklySchedule = () => {
    const schedule: Worksheet[][] = [];
    for (let i = 0; i < selectedWeeks; i++) {
      const start = i * worksheetsPerWeek;
      const end = Math.min(start + worksheetsPerWeek, worksheets.length);
      schedule.push(worksheets.slice(start, end));
    }
    return schedule;
  };

  const weeklySchedule = getWeeklySchedule();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50">
      {/* Header */}
      <header className="bg-white border-b border-rose-100 print:border-b-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="inline-flex items-center text-rose-600 hover:text-rose-700 mb-3 text-sm font-medium whitespace-nowrap">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
              <h1 className="text-4xl font-bold text-gray-900">Grade 11 Study Plan</h1>
              <p className="text-lg text-gray-600 mt-2">Algebra 2 & Pre-Calculus Prep — Structured Weekly Schedule</p>
            </div>
            <button
              onClick={handlePrint}
              className="hidden sm:flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium whitespace-nowrap print:hidden"
            >
              <i className="ri-printer-line"></i>
              Print Plan
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Schedule Selector */}
        <div className="bg-white rounded-xl shadow-sm border border-rose-100 p-8 mb-8 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Study Timeline</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {scheduleOptions.map((option) => (
              <button
                key={option.weeks}
                onClick={() => setSelectedWeeks(option.weeks)}
                className={`p-6 rounded-xl border-2 transition-all text-left ${
                  selectedWeeks === option.weeks
                    ? 'border-rose-500 bg-rose-50 shadow-md'
                    : 'border-gray-200 hover:border-rose-300 hover:bg-rose-50/50'
                }`}
              >
                <div className="text-3xl font-bold text-rose-600 mb-2">{option.weeks}</div>
                <div className="font-semibold text-gray-900 mb-1">{option.label}</div>
                <div className="text-sm text-gray-600">{option.description}</div>
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-8 border-t border-rose-100">
            <div className="text-center">
              <div className="text-3xl font-bold text-rose-600">{worksheets.length}</div>
              <div className="text-sm text-gray-600 mt-1">Total Worksheets</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-rose-600">{totalPages}</div>
              <div className="text-sm text-gray-600 mt-1">Total Pages</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-rose-600">{estimatedHours}h</div>
              <div className="text-sm text-gray-600 mt-1">Estimated Time</div>
            </div>
          </div>
        </div>

        {/* Weekly Schedule */}
        <div className="space-y-6 mb-8">
          {weeklySchedule.map((week, weekIndex) => {
            const weekPages = week.reduce((sum, w) => sum + w.pages, 0);
            const weekHours = Math.round((weekPages * 0.5 + week.length * 0.25) * 10) / 10;

            return (
              <div
                key={weekIndex}
                className="bg-white rounded-xl shadow-sm border border-rose-100 overflow-hidden print:break-inside-avoid"
              >
                <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">Week {weekIndex + 1}</h3>
                    <div className="flex items-center gap-6 text-white text-sm">
                      <span className="flex items-center gap-2">
                        <i className="ri-file-list-line"></i>
                        {week.length} worksheets
                      </span>
                      <span className="flex items-center gap-2">
                        <i className="ri-pages-line"></i>
                        {weekPages} pages
                      </span>
                      <span className="flex items-center gap-2">
                        <i className="ri-time-line"></i>
                        ~{weekHours}h
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-3">
                    {week.map((worksheet, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 bg-rose-50/50 rounded-lg border border-rose-100 hover:bg-rose-50 transition-colors"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{worksheet.title}</div>
                            <div className="text-sm text-gray-600 mt-1">{worksheet.topic}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                              worksheet.difficulty === 'Easy'
                                ? 'bg-green-100 text-green-700'
                                : worksheet.difficulty === 'Medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {worksheet.difficulty}
                          </span>
                          <span className="text-sm text-gray-600 whitespace-nowrap">{worksheet.pages} pages</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Study Tips */}
        <div className="bg-white rounded-xl shadow-sm border border-rose-100 p-8 mb-8 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Study Tips for Success</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {studyTips.map((tip, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                  <i className={`${tip.icon} text-2xl`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:hidden">
          <Link
            to="/grade/11"
            className="bg-white rounded-xl shadow-sm border border-rose-100 p-6 hover:shadow-md transition-shadow group"
          >
            <div className="w-12 h-12 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center mb-4 group-hover:bg-rose-600 group-hover:text-white transition-colors">
              <i className="ri-book-open-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Grade 11 Curriculum</h3>
            <p className="text-sm text-gray-600">Access full lessons and practice problems</p>
          </Link>

          <Link
            to="/grade-11-worksheets"
            className="bg-white rounded-xl shadow-sm border border-rose-100 p-6 hover:shadow-md transition-shadow group"
          >
            <div className="w-12 h-12 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center mb-4 group-hover:bg-rose-600 group-hover:text-white transition-colors">
              <i className="ri-file-list-3-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Browse Worksheets</h3>
            <p className="text-sm text-gray-600">Download printable practice sheets</p>
          </Link>

          <Link
            to="/grade-11-progress"
            className="bg-white rounded-xl shadow-sm border border-rose-100 p-6 hover:shadow-md transition-shadow group"
          >
            <div className="w-12 h-12 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center mb-4 group-hover:bg-rose-600 group-hover:text-white transition-colors">
              <i className="ri-bar-chart-box-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Progress Tracker</h3>
            <p className="text-sm text-gray-600">Track completed worksheets and skills</p>
          </Link>
        </div>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:break-inside-avoid {
            break-inside: avoid;
          }
          .print\\:border-b-2 {
            border-bottom-width: 2px;
          }
        }
      `}</style>
    </div>
  );
}